import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit, Trash2, Download, MapPin, Calendar, CreditCard, CheckCircle2, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { generateReceiptPDF } from "@/lib/pdfGenerator";
import { deleteReceiptAndSyncSummary } from "@/lib/receiptUtils";
import DeleteReceiptDialog from "@/components/DeleteReceiptDialog";
import ReceiptDetailSkeleton from "@/components/ReceiptDetailSkeleton";
import { validateUserFilePath } from "@/lib/receiptValidation";

interface ReceiptData {
  id: string;
  merchant_name: string | null;
  date: string;
  file_path: string;
  category: string;
  amount: number;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  currency: string | null;
  line_items: any;
  parsed_json: any;
  status: string;
  item_categories: any;
  deductible_items: any;
  non_deductible_items: any;
  tax_savings_estimate: number | null;
  deduction_explanation: string | null;
  confidence_score: number | null;
  is_duplicate: boolean | null;
}

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [urlExpiry, setUrlExpiry] = useState<number>(0);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchReceipt();
  }, [id]);

  const fetchReceipt = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/");
        return;
      }

      const { data, error } = await supabase
        .from("receipts")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id) // Security: user_id filter
        .single();

      if (error) throw error;

      // Security: Validate file path belongs to user
      if (data.file_path && !validateUserFilePath(data.file_path, user.id)) {
        throw new Error("Unauthorized access to receipt");
      }

      setReceipt(data);

      // Get signed URL for image with 60-minute cache
      if (data.file_path) {
        // Check if cached URL is still valid
        if (!imageUrl || Date.now() >= urlExpiry) {
          const { data: signedUrlData } = await supabase.storage
            .from("receipts")
            .createSignedUrl(data.file_path, 3600);

          if (signedUrlData) {
            setImageUrl(signedUrlData.signedUrl);
            setUrlExpiry(Date.now() + 3600 * 1000); // Cache for 60 minutes
          }
        }
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to fetch receipt:", error);
      }
      toast({
        title: "Error",
        description: "Failed to load receipt details",
        variant: "destructive",
      });
      navigate("/receipts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!receipt) return;
    setDeleteDialogOpen(false);
    setDeleting(true);

    try {
      await deleteReceiptAndSyncSummary(receipt.id);

      toast({
        title: "Success",
        description: "Receipt deleted successfully",
      });
      navigate("/receipts");
    } catch (error: any) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to delete receipt:", error);
      }
      toast({
        title: "Error",
        description: "Failed to delete receipt",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const getBusinessExpenseStatus = () => {
    if (!receipt?.parsed_json) return null;

    const { business_expense_assessment } = receipt.parsed_json;
    if (!business_expense_assessment) return null;

    return business_expense_assessment;
  };

  const handleDownloadPDF = async () => {
    if (!receipt) return;

    try {
      await generateReceiptPDF({
        merchant_name: receipt.merchant_name || "Unknown Merchant",
        store_address: receipt.parsed_json?.store_address,
        date: receipt.date,
        total: receipt.total || receipt.amount,
        subtotal: receipt.subtotal || receipt.amount,
        tax: receipt.tax || 0,
        currency: receipt.currency || "CAD",
        line_items: receipt.line_items,
        item_categories: receipt.item_categories,
        deductible_items: receipt.deductible_items,
        non_deductible_items: receipt.non_deductible_items,
        tax_savings_estimate: receipt.tax_savings_estimate || undefined,
        deduction_explanation: receipt.deduction_explanation || undefined,
        confidence_score: receipt.confidence_score || undefined,
        parsed_json: receipt.parsed_json,
        imageUrl: imageUrl,
      });

      toast({
        title: "Success",
        description: "PDF downloaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <ReceiptDetailSkeleton />;
  }

  if (!receipt) return null;

  const businessExpense = getBusinessExpenseStatus();
  const lineItems = Array.isArray(receipt.line_items) ? receipt.line_items : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/receipts")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Receipt Details</h1>
        </div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Receipt Image & Store Info */}
        <Card className="p-6">
          <div className="flex gap-6">
            {imageUrl && (
              <img 
                src={imageUrl} 
                alt="Receipt"
                className="w-32 h-32 object-cover rounded-lg"
              />
            )}
            <div className="flex-1 space-y-2">
              <div className="flex items-start justify-between">
                <h2 className="text-2xl font-bold">{receipt.merchant_name || "Unknown Merchant"}</h2>
                {receipt.is_duplicate && (
                  <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                    Possible Duplicate
                  </Badge>
                )}
              </div>
              {receipt.parsed_json?.store_address && (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{receipt.parsed_json.store_address}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <p className="text-sm">{new Date(receipt.date).toLocaleDateString()}</p>
              </div>
              {receipt.confidence_score !== null && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-muted-foreground">OCR Confidence:</span>
                  <Badge variant={receipt.confidence_score >= 80 ? "default" : receipt.confidence_score >= 60 ? "secondary" : "destructive"}>
                    {receipt.confidence_score}%
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Low Confidence Warning */}
        {receipt.confidence_score !== null && receipt.confidence_score < 60 && (
          <Card className="p-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900">Low Confidence Score</h4>
                <p className="text-sm text-yellow-800">
                  The OCR had difficulty reading this receipt. Please verify all details are accurate.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Tax Savings Highlight */}
        {receipt.tax_savings_estimate !== null && receipt.tax_savings_estimate > 0 && (
          <Card className="p-6 bg-[#22c25c]/5 border-[#22c25c]/20">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[#22c25c]">Estimated Tax Savings</h3>
                <p className="text-sm text-muted-foreground">Based on eligible business expenses</p>
              </div>
              <div className="text-3xl font-bold text-[#22c25c]">
                ${receipt.tax_savings_estimate.toFixed(2)}
              </div>
            </div>
          </Card>
        )}

        {/* Line Items with Categories */}
        {lineItems.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Items</h3>
            <div className="space-y-3">
              {lineItems.map((item: any, index: number) => {
                const itemCategories = Array.isArray(receipt.item_categories) ? receipt.item_categories : [];
                const category = itemCategories[index];
                const deductibleItems = Array.isArray(receipt.deductible_items) ? receipt.deductible_items : [];
                const isDeductible = deductibleItems.includes(item.name);
                
                return (
                  <div key={index} className="flex justify-between items-start p-3 rounded-lg bg-muted/30">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{item.name}</p>
                        {isDeductible && (
                          <Badge className="bg-[#22c25c] text-white text-xs">Tax Deductible</Badge>
                        )}
                      </div>
                      {item.quantity && <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>}
                      {category && (
                        <Badge variant="outline" className="mt-1 text-xs">{category}</Badge>
                      )}
                    </div>
                    <p className="font-semibold">${item.price?.toFixed(2) || '0.00'}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Totals */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Summary</h3>
          <div className="space-y-3">
            {receipt.subtotal !== null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${receipt.subtotal.toFixed(2)}</span>
              </div>
            )}
            {receipt.tax !== null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span>${receipt.tax.toFixed(2)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>${(receipt.total || receipt.amount).toFixed(2)}</span>
            </div>
            {receipt.parsed_json?.payment_method && (
              <div className="flex items-center gap-2 text-muted-foreground pt-2">
                <CreditCard className="h-4 w-4" />
                <span className="text-sm">{receipt.parsed_json.payment_method}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Business Expense Assessment */}
        {businessExpense && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Business Expense Evaluation</h3>
            
            {businessExpense.overall_status === 'yes' && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-[#22c25c]/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-[#22c25c]" />
                  </div>
                  <div>
                    <Badge className="bg-[#22c25c] text-white">Business Expense</Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      This receipt qualifies as a business expense
                    </p>
                  </div>
                </div>
                {businessExpense.explanation && (
                  <p className="text-sm text-muted-foreground pl-15">{businessExpense.explanation}</p>
                )}
              </div>
            )}

            {businessExpense.overall_status === 'no' && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <div>
                    <Badge variant="destructive">Not Business Expense</Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      This receipt does not qualify as a business expense
                    </p>
                  </div>
                </div>
                {businessExpense.explanation && (
                  <p className="text-sm text-muted-foreground pl-15">{businessExpense.explanation}</p>
                )}
              </div>
            )}

            {businessExpense.overall_status === 'mixed' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <Badge className="bg-yellow-100 text-yellow-800">Mixed</Badge>
                    <p className="text-sm text-muted-foreground mt-1">
                      Some items qualify, others don't
                    </p>
                  </div>
                </div>

                {businessExpense.qualifying_items && businessExpense.qualifying_items.length > 0 && (
                  <div className="pl-15">
                    <p className="font-medium text-sm mb-2">✓ These items qualify:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      {businessExpense.qualifying_items.map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {businessExpense.non_qualifying_items && businessExpense.non_qualifying_items.length > 0 && (
                  <div className="pl-15">
                    <p className="font-medium text-sm mb-2">✗ These items do NOT qualify:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      {businessExpense.non_qualifying_items.map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Tax Deduction Details */}
        {receipt.deduction_explanation && (
          <Card className="p-6 bg-blue-50 border-blue-200">
            <h3 className="text-lg font-semibold mb-3 text-blue-900">Tax Deduction Analysis</h3>
            <p className="text-sm text-blue-800">{receipt.deduction_explanation}</p>
            {receipt.tax_savings_estimate !== null && receipt.tax_savings_estimate > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-blue-900">Potential Tax Savings:</span>
                  <span className="text-lg font-bold text-[#22c25c]">${receipt.tax_savings_estimate.toFixed(2)}</span>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => navigate(`/receipt/${receipt.id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Receipt
          </Button>
          <Button 
            variant="outline" 
            className="flex-1" 
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
          >
            {downloadingPdf ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </>
            )}
          </Button>
          <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <DeleteReceiptDialog
        open={deleteDialogOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        merchantName={receipt.merchant_name || "Unknown Merchant"}
      />
    </div>
  );
}
